// src/pos-app/StationSettingsPrinter.js
//
// Device Printer Config Task 4 — the PRINTER sub-section of
// StationSettingsSheet.js's settings stage. Lets a manager, standing at the
// till, either pick one of the tenant's already-registered Epson printers OR
// add a brand-new one from scratch (name, role, IP) without ever opening the
// web portal — the probe only works from a device on the printer's own LAN,
// which is exactly where this sheet runs. Rendered only inside the sheet's
// manager-gated settings stage (see StationSettingsSheet.js) — no auth of
// its own.
//
// Task 7 — per-role assignment. A device can now be assigned a printer PER
// ROLE (`printerIds: { receipt, kitchen, bar }`), not just one printer
// total. The common restaurant layout is a single till with no KDS screen
// driving THREE printers at once — receipt at the counter, kitchen, bar —
// which a single printerId could never express (only one of the three could
// ever be named, and the other two would silently fall back to the
// tenant-wide "oldest active printer for this role" lookup, the exact bug
// this feature exists to fix). So the UI is now one independent picker per
// role, each with its own probe and test-page button, so a manager can
// verify each printer independently. `printerIds`/`onChange(role, id)`
// mirror the station/kdsQueue pattern in the parent — this component never
// persists anything itself, it only reports the selected id for a role
// upward so the parent's single Save button (saveStation) writes
// station + kdsQueue + printerIds together.
//
// Clicking an already-selected printer in its role's list deselects it
// (onChange(role, null)) — the only way to intentionally return a role to
// the tenant+role fallback once something has been picked, short of
// deleting the printer itself. A `clear_printer_assignment_hint` line makes
// this discoverable — otherwise the checkmark disappearing looks identical
// to picking a different printer, and a manager has no way to tell they can
// undo a wrong assignment.
//
// Fix round 1: `handleProbeSelected`/`handleTestPrint` guard on
// `probingRole === r` / `testingRole === r` (not "is ANY role busy") so
// they match the per-role `disabled` attributes below — a probe/test in
// flight on one role must never silently swallow a click on another role's
// still-enabled button.
//
// Star CloudPRNT printers are filtered out of `listPrinters()`'s response —
// they poll the backend and have no device-side step, so they don't belong
// in a "set this device's printer up" surface.
import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { printingService } from '../services/api';
import { probePrinter } from '../utils/eposClient';
import { sendPayload } from '../utils/printTransport';
import { DEFAULT_PRINTER_IDS } from '../station/stationConfig';
import { printerForCategory } from '../utils/ticketRoles';

// The slots this sheet offers. 'receipt' is always present — every till
// needs one. The rest are the tenant's kitchen-display CATEGORIES, so a shop
// with a grill station and a dessert station can give each its own paper.
//
// Falls back to the legacy kitchen/bar pair when no categories are supplied
// (the fetch failed, or an older caller has not passed them): losing the
// printer UI entirely would be a worse failure than showing two generic
// slots, and any category with no printer of its own falls back to the
// kitchen printer at print time anyway.
const LEGACY_ROLES = ['kitchen', 'bar'];

const roleOptionsFor = (categories) => (
    ['receipt', ...(categories && categories.length ? categories : LEGACY_ROLES)]
);

// Category names are tenant data ("PIRI'S GRILLED COLLECTION"), not
// translation keys, so only the fixed roles go through t().
const FIXED_ROLE_LABELS = ['receipt', 'kitchen', 'bar'];

// 'receipt' is the customer's till receipt. The bare word sat ambiguously
// next to a list of ticket printers that are also, technically, receipts.
const ROLE_LABEL_KEY = { receipt: 'till_receipt' };

// Transports this device drives directly. CloudPRNT is excluded: those
// printers poll the backend and have nothing to set up here.
const DEVICE_PROTOCOLS = ['epson', 'escpos'];

// A conservative dotted-quad check — good enough to catch "empty", "not an
// IP at all", and "octet out of range" before anything hits the network.
// Not RFC-perfect (doesn't reject e.g. leading zeros), but that's fine: a
// probe against a slightly-odd-but-still-numeric address just fails
// cleanly like any other unreachable address.
const IPV4_RE = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
export function isValidIp(value) {
    const match = IPV4_RE.exec((value || '').trim());
    if (!match) return false;
    return match.slice(1, 5).every((octet) => Number(octet) >= 0 && Number(octet) <= 255);
}

// Maps an eposClient probePrinter() result to a plain-language outcome plus
// a severity level for the message box's color. Mirrors
// PortalSettingsPage.js's epsonProbeOutcome — same wording, same three
// cases — so a tenant sees identical language whether they configured a
// printer from the portal or from this device. The probe is advice only:
// none of these branches affect whether the printer gets saved.
export function probeOutcome(result, t) {
    if (!result) return null;

    if (result.reachable && result.eposAvailable) {
        if (result.model) {
            return {
                level: 'success',
                protocol: 'epson',
                text: `${t('epson_probe_detected_prefix')} ${result.model} · ${t('epson_probe_epos_available')}`,
            };
        }
        return {
            level: 'success',
            protocol: 'epson',
            text: t('epson_probe_epos_available_unknown_model'),
        };
    }

    // No ePOS service, but the raw print port answers — the TM-T20X case.
    // Perfectly printable, just over a different transport.
    if (result.reachable && result.rawAvailable) {
        return {
            level: 'success',
            protocol: 'escpos',
            text: result.model
                ? `${t('epson_probe_detected_prefix')} ${result.model} · ${t('probe_raw_available')}`
                : t('probe_raw_available'),
        };
    }

    // We could not ask about raw printing at all — this build has no native
    // socket plugin. Saying "not accepting print jobs" here would be a
    // false negative about the PRINTER when the gap is in the APP, so say
    // which it is.
    if (result.reachable && result.rawStatus === 'unavailable') {
        return {
            level: 'warn',
            protocol: null,
            text: result.model
                ? `${t('epson_probe_detected_prefix')} ${result.model} · ${t('probe_raw_unavailable')}`
                : t('probe_raw_unavailable'),
        };
    }

    // Reachable, but neither transport answers. The model — read from the
    // device's own web page — is the single most useful thing we can say
    // here, and it used to be captured and then thrown away, which is why
    // diagnosing the TM-T20X took a full evening.
    if (result.reachable) {
        return {
            level: 'warn',
            protocol: null,
            text: result.model
                ? `${t('epson_probe_detected_prefix')} ${result.model} · ${t('probe_no_protocol')}`
                : t('epson_probe_not_a_printer'),
        };
    }

    return { level: 'error', protocol: null, text: t('epson_probe_unreachable') };
}

const CheckIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

const PrinterIcon = (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 9V3h12v6" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <path d="M6 14h12v8H6z" />
    </svg>
);

export default function StationSettingsPrinter({ printerIds, onChange, categories }) {
    const { t } = useLanguage();
    const ids = printerIds || DEFAULT_PRINTER_IDS;
    const roleOptions = roleOptionsFor(categories);
    const labelFor = (r) => (FIXED_ROLE_LABELS.includes(r) ? t(ROLE_LABEL_KEY[r] || r) : r);

    // Every till needs a receipt printer, so that slot is always open.
    // Kitchen ticket printers are configured once and rarely revisited, and
    // a tenant may have fifteen categories — each slot carries a printer
    // card, an add link and two action buttons, so rendering them all
    // expanded turned this sheet into several screens of scrolling.
    const categoryRoles = roleOptions.filter((r) => r !== 'receipt');
    const [categoriesOpen, setCategoriesOpen] = useState(false);
    // Each category opens and closes independently, all closed by default.
    // The previous single toggle opened them ALL at once, which just moved
    // the wall of controls behind one tap: five categories still meant five
    // printer cards, five add links and four action buttons on screen.
    // Deliberately NOT an accordion — setting up a new shop means comparing
    // two stations side by side, and forcing one shut to open another fights
    // that for no benefit.
    const [openCategories, setOpenCategories] = useState(() => new Set());
    const toggleCategory = (r) => setOpenCategories((prev) => {
        const next = new Set(prev);
        if (next.has(r)) next.delete(r); else next.add(r);
        return next;
    });
    // The receipt slot is always rendered in full; categories render as
    // summary rows and expand individually below.
    const visibleRoles = roleOptions.filter((r) => r === 'receipt');

    const [printers, setPrinters] = useState([]);
    const [deletingId, setDeletingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [mode, setMode] = useState('choose'); // 'choose' | 'add'

    // add-from-scratch form
    const [name, setName] = useState('');
    const [role, setRole] = useState('receipt');
    const [ip, setIp] = useState('');
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState('');

    // probe / test-print outcome, keyed per role so probing/testing one
    // role's printer never overwrites another role's result.
    const [probingRole, setProbingRole] = useState(null);
    const [probeResults, setProbeResults] = useState({});
    const [testingRole, setTestingRole] = useState(null);
    const [testResults, setTestResults] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setLoadError('');
            try {
                const list = await printingService.listPrinters();
                if (cancelled) return;
                // Star CloudPRNT printers have no device-side step — never
                // list them here. Both direct-to-device transports do:
                // 'epson' POSTs ePOS-Print XML, 'escpos' writes raw bytes
                // to port 9100. Filtering on 'epson' alone would make a
                // printer disappear from this page the moment the probe
                // detected it as escpos.
                setPrinters(
                    (list || []).filter((p) => DEVICE_PROTOCOLS.includes(p.protocol))
                );
            } catch (err) {
                if (cancelled) return;
                setLoadError(t('failed_to_load_printers'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // Fetch once on mount only. Deliberately NOT depending on `t` — this
        // is a device-config screen the manager glances at once per PIN
        // session, not something that needs to re-fetch on a language
        // toggle, and `t` is not guaranteed to be a stable reference across
        // renders (some LanguageContext consumers/mocks vend a fresh
        // function each render), which would otherwise re-fire this effect
        // on every state update below and loop forever.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearRoleResults = (r) => {
        setProbeResults((prev) => ({ ...prev, [r]: null }));
        setTestResults((prev) => ({ ...prev, [r]: null }));
    };

    const selectPrinter = useCallback((r, id) => {
        // Clicking the already-selected printer deselects it — the only way
        // to intentionally send a role back to the tenant+role fallback.
        const next = ids[r] === id ? null : id;
        onChange(r, next);
        clearRoleResults(r);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onChange, ids]);

    const openAdd = (forRole) => {
        setMode('add');
        setRole(forRole);
        setAddError('');
    };

    const handleAdd = async () => {
        if (adding) return;
        const trimmedName = name.trim();
        const trimmedIp = ip.trim();
        // Defense in depth: the Add button is already disabled while either
        // field is empty, but never call the API on an incomplete form
        // regardless of how this handler gets invoked.
        if (!trimmedName || !trimmedIp) return;

        setAddError('');
        if (!isValidIp(trimmedIp)) {
            setAddError(t('invalid_ip_address'));
            return;
        }

        setAdding(true);
        try {
            // Probe BEFORE saving so the tenant sees what answered — but the
            // save happens regardless of the outcome. A printer that's
            // switched off right now isn't a configuration error.
            const raw = await probePrinter(trimmedIp);
            const outcome = probeOutcome(raw, t);

            let printer;
            try {
                printer = await printingService.createPrinter(trimmedName, role, {
                    protocol: 'epson',
                    ip_address: trimmedIp,
                });
            } catch (err) {
                setAddError(t('failed_to_create_printer'));
                return;
            }

            setPrinters((prev) => [...prev, printer]);
            // A newly added printer becomes ITS OWN role's assignment — the
            // role it was actually created with (the form's own role
            // picker), not necessarily whichever section's "+" link opened
            // the form (that only sets the form's starting value).
            onChange(printer.role, printer.id);
            // Reveal the section the new printer landed in. Adding a Grill
            // printer and being returned to a collapsed list, with no sign
            // of it, reads as the add having failed.
            if (printer.role !== 'receipt') {
                setCategoriesOpen(true);
                // …and open the specific category, or the new printer lands in
                // a collapsed row and the add reads as having failed.
                setOpenCategories((prev) => new Set(prev).add(printer.role));
            }
            setProbeResults((prev) => ({ ...prev, [printer.role]: outcome }));
            setTestResults((prev) => ({ ...prev, [printer.role]: null }));
            setMode('choose');
            setName('');
            setIp('');
            setRole('receipt');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (r, printer) => {
        // Confirmed, because this is destructive and the sheet is a
        // thumb-sized surface on a till. The gate on the sheet itself
        // (device_config) already keeps this away from ordinary staff.
        // eslint-disable-next-line no-alert, no-restricted-globals
        if (!window.confirm(t('delete_printer_confirm'))) return;
        setDeletingId(printer.id);
        try {
            await printingService.deletePrinter(printer.id);
            setPrinters((prev) => prev.filter((p) => p.id !== printer.id));
            // Clear the assignment if this device was pointing at it —
            // otherwise the station keeps an id that no longer resolves and
            // prints fall back silently.
            if (ids[r] === printer.id) onChange(r, null);
            setProbeResults((prev) => ({ ...prev, [r]: null }));
            setTestResults((prev) => ({ ...prev, [r]: null }));
        } catch (e) {
            setAddError(t('delete_printer_failed'));
        } finally {
            setDeletingId(null);
        }
    };

    const handleProbeSelected = async (r) => {
        const printer = printers.find((p) => p.id === ids[r]);
        // Scoped to THIS role only — a probe in flight on another role must
        // never block this one; the disabled attribute below is scoped the
        // same way (`probingRole === r`), and this guard has to match it or
        // the button looks enabled but silently does nothing.
        if (!printer || probingRole === r) return;
        setProbingRole(r);
        setProbeResults((prev) => ({ ...prev, [r]: null }));
        try {
            const raw = await probePrinter(printer.ip_address, printer.port);
            const outcome = probeOutcome(raw, t);
            setProbeResults((prev) => ({ ...prev, [r]: outcome }));

            // Save what the probe worked out, so printing dispatches over
            // the right transport without anyone on site needing to know
            // what a protocol is.
            if (outcome && outcome.protocol && outcome.protocol !== printer.protocol) {
                try {
                    await printingService.setPrinterProtocol(printer.id, outcome.protocol);
                    setPrinters((prev) => prev.map(
                        (p) => (p.id === printer.id
                            ? { ...p, protocol: outcome.protocol }
                            : p)
                    ));
                } catch (e) {
                    // Detection is advice. Failing to persist it must not
                    // replace the probe's own result with an error the
                    // tenant can do nothing about.
                }
            }
        } finally {
            setProbingRole(null);
        }
    };

    // The drawer is plugged into ONE printer's DK port, so this is a fact
    // about that printer, not about the tenant — a shop with two tills has
    // two drawers, and a kitchen/bar printer never has one. Optimistic: the
    // toggle itself is the whole feedback, and a failed write is trivially
    // recoverable by tapping again.
    const handleToggleDrawer = async (printer) => {
        const next = !printer.has_drawer;
        setPrinters((prev) => prev.map(
            (p) => (p.id === printer.id ? { ...p, has_drawer: next } : p)
        ));
        try {
            await printingService.setPrinterDrawer(printer.id, next);
        } catch (e) {
            setPrinters((prev) => prev.map(
                (p) => (p.id === printer.id ? { ...p, has_drawer: !next } : p)
            ));
        }
    };

    const handleTestPrint = async (r) => {
        const printer = printers.find((p) => p.id === ids[r]);
        // Scoped to THIS role only — same reasoning as handleProbeSelected.
        if (!printer || testingRole === r) return;
        setTestingRole(r);
        setTestResults((prev) => ({ ...prev, [r]: null }));
        try {
            const payload = await printingService.getEposTestPayload(printer.id);
            const result = await sendPayload(payload);
            if (result.success) {
                setTestResults((prev) => ({ ...prev, [r]: { level: 'success', text: t('test_print_sent') } }));
            } else {
                const code = result.code || result.reason || '';
                setTestResults((prev) => ({
                    ...prev,
                    [r]: { level: 'error', text: `${t('test_print_failed')}${code ? ` (${code})` : ''}` },
                }));
            }
        } catch (err) {
            setTestResults((prev) => ({ ...prev, [r]: { level: 'error', text: t('test_print_failed') } }));
        } finally {
            setTestingRole(null);
        }
    };

    const canSubmitAdd = name.trim().length > 0 && ip.trim().length > 0 && !adding;

    // Extracted so the same slot markup serves the always-visible receipt
    // printer and the one expanded KDS category. Defined as a function
    // rather than duplicated, so probe/test/add behaviour can never drift
    // between the two places a slot appears.
    const renderRoleSection = (r) => {
                const rolePrinters = printers.filter((p) => p.role === r);
                const selectedId = ids[r] || null;
                const selectedPrinter = selectedId
                    ? printers.find((p) => p.id === selectedId)
                    : null;
                const probeResult = probeResults[r];
                const testResult = testResults[r];
                return (
                    <div className="sts-printer-role-section" key={r}>
                        <div className="sts-label">{labelFor(r)}</div>

                        {rolePrinters.length === 0 ? (
                            <div>
                                <p className="sts-sub" style={{ margin: '0 0 8px' }}>{t('role_no_printer_hint')}</p>
                                <button type="button" className="sts-printer-add-link" onClick={() => openAdd(r)}>
                                    + {t('add_printer')}
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div className="sts-printer-list" role="group" aria-label={labelFor(r)}>
                                    {rolePrinters.map((p) => (
                                        <div className="sts-printer-entry" key={p.id}>
                                            <button
                                                type="button"
                                                className={`sts-printer-item${selectedId === p.id ? ' on' : ''}`}
                                                aria-pressed={selectedId === p.id}
                                                onClick={() => selectPrinter(r, p.id)}
                                            >
                                                <span>
                                                    <span className="sts-printer-item-name">{p.name}</span>
                                                    <span className="sts-printer-item-meta">{p.ip_address}</span>
                                                </span>
                                                {selectedId === p.id ? <span className="sts-printer-check">{CheckIcon}</span> : null}
                                            </button>
                                            <button
                                                type="button"
                                                className="sts-printer-del"
                                                aria-label={`${t('delete')} ${p.name}`}
                                                disabled={deletingId === p.id}
                                                onClick={() => handleDelete(r, p)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {selectedId && (
                                    <p className="sts-sub" style={{ margin: '4px 0 8px' }}>
                                        {t('clear_printer_assignment_hint')}
                                    </p>
                                )}

                                <button type="button" className="sts-printer-add-link" onClick={() => openAdd(r)}>
                                    + {t('add_printer')}
                                </button>

                                <div className="sts-printer-actions">
                                    <button
                                        type="button"
                                        className="sts-printer-action-btn"
                                        onClick={() => handleProbeSelected(r)}
                                        disabled={!selectedId || probingRole === r}
                                    >
                                        {t('probe_printer')}
                                    </button>
                                    <button
                                        type="button"
                                        className="sts-printer-action-btn"
                                        onClick={() => handleTestPrint(r)}
                                        disabled={!selectedId || testingRole === r}
                                    >
                                        {t('print_test_page')}
                                    </button>
                                </div>

                                {/* Receipt-role only: the drawer's DK port is on the till
                                    printer at the counter. Kitchen/bar printers have nothing
                                    to plug a drawer into. */}
                                {r === 'receipt' && selectedPrinter && (
                                    <label className="sts-drawer-toggle" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                                        <input
                                            type="checkbox"
                                            checked={!!selectedPrinter.has_drawer}
                                            onChange={() => handleToggleDrawer(selectedPrinter)}
                                        />
                                        {t('cash_drawer_attached')}
                                    </label>
                                )}

                                {probeResult && (
                                    <div className={`sts-printer-result ${probeResult.level}`} role="status">
                                        <span>{probeResult.text}</span>
                                    </div>
                                )}
                                {testResult && (
                                    <div className={`sts-printer-result ${testResult.level}`} role="status">
                                        <span>{testResult.text}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
    };

    return (
        <div className="sts-row sts-printer-row">
            <div className="sts-label">{t('printer')}</div>

            {loading ? (
                <p className="sts-sub" style={{ margin: 0 }}>{t('loading')}</p>
            ) : loadError ? (
                <div className="sts-error" role="alert">
                    <span>{loadError}</span>
                </div>
            ) : mode === 'add' ? (
                <div className="sts-printer-form">
                    <div className="sts-field">
                        <label className="sts-label" htmlFor="sts-printer-name">{t('printer_name')}</label>
                        <input
                            id="sts-printer-name"
                            className="sts-input"
                            type="text"
                            placeholder={t('printer_name_placeholder')}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={adding}
                        />
                    </div>
                    <div className="sts-field">
                        <div className="sts-label">{t('role')}</div>
                        <div className="sts-seg" role="group" aria-label={t('role')}>
                            {roleOptions.map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    className={role === r ? 'on' : ''}
                                    aria-pressed={role === r}
                                    disabled={adding}
                                    onClick={() => setRole(r)}
                                >
                                    {labelFor(r)}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="sts-field">
                        <label className="sts-label" htmlFor="sts-printer-ip">{t('printer_ip_address')}</label>
                        <input
                            id="sts-printer-ip"
                            className="sts-input"
                            type="text"
                            placeholder={t('printer_ip_placeholder')}
                            value={ip}
                            onChange={(e) => setIp(e.target.value)}
                            disabled={adding}
                        />
                    </div>

                    {addError && (
                        <div className="sts-error" role="alert">
                            <span>{addError}</span>
                        </div>
                    )}

                    <div className="sts-printer-form-actions">
                        <button
                            type="button"
                            className="sts-cancel"
                            onClick={() => { setMode('choose'); setAddError(''); }}
                            disabled={adding}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            className="sts-save"
                            onClick={handleAdd}
                            disabled={!canSubmitAdd}
                        >
                            {t('add_printer')}
                        </button>
                    </div>
                </div>
            ) : printers.length === 0 ? (
                <div className="sts-printer-empty">
                    {PrinterIcon}
                    <p>{t('no_printers_yet')}</p>
                    <p>{t('no_printers_hint')}</p>
                    <button type="button" className="sts-printer-empty-cta" onClick={() => openAdd('receipt')}>
                        {t('add_printer')}
                    </button>
                </div>
            ) : (
                <div className="sts-printer-roles">
                    {/* One role slot, rendered for the always-open
                        receipt AND for whichever category is expanded. */}
                    {visibleRoles.map((r) => renderRoleSection(r))}

                    {categoryRoles.length > 0 && (
                        <div className="sts-kds-printers">
                            <button
                                type="button"
                                className="sts-printer-add-link"
                                data-testid="category-printers-toggle"
                                aria-expanded={categoriesOpen}
                                onClick={() => setCategoriesOpen((open) => !open)}
                            >
                                {categoriesOpen ? '\u25be' : '\u25b8'}{' '}
                                {t('kds_printers')} ({categoryRoles.length})
                            </button>

                            {categoriesOpen && categoryRoles.map((r) => {
                                // The printer this category's ticket would
                                // ACTUALLY come out of, resolved by the same
                                // chain PrintingUtils.printCategoryTicket
                                // walks. The row used to look only for a
                                // printer whose role matched `r` exactly and
                                // label every miss "Falls back to kitchen" —
                                // false whenever no kitchen printer existed,
                                // false for the kitchen row itself, and false
                                // for a category served by a legacy
                                // lowercase role ("Bar" -> 'bar').
                                const assigned = printerForCategory(printers, r, ids[r]);
                                const isOpen = openCategories.has(r);
                                return (
                                    <div key={r} className="sts-cat">
                                        <button
                                            type="button"
                                            className="sts-cat-row"
                                            data-testid={`category-row-${r}`}
                                            aria-expanded={isOpen}
                                            onClick={() => toggleCategory(r)}
                                        >
                                            <span className="sts-cat-name">{labelFor(r)}</span>
                                            <span className={`sts-cat-to${assigned ? '' : ' fallback'}`}>
                                                {assigned
                                                    ? (assigned.role === r
                                                        ? assigned.name
                                                        // Reached through the fallback chain rather
                                                        // than a printer of its own — say so, and say
                                                        // which, so the shop can tell a deliberate
                                                        // setup from an accident.
                                                        : `${t('falls_back_to')} ${assigned.name}`)
                                                    : t('no_ticket_printer')}
                                            </span>
                                            <span className="sts-cat-chev">{isOpen ? '\u25be' : '\u203a'}</span>
                                        </button>
                                        {isOpen && (
                                            <div className="sts-cat-body">
                                                {renderRoleSection(r)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
